import { z } from "zod";

import type { JsonValue } from "../types";

import { jsonValueSchema } from "../schemas/jsonValue";

export interface ImplementationSubtaskContext {
  id: string;
  issue_key: string | null;
  title: string;
  description: string;
  repository_slug: string;
  reference_repository_slugs: string[];
  dependencies: string[];
  attachment_refs: string[];
  attachments?: ImplementationAttachmentContext[];
  self_assessed_risk: string | null;
  size_symbol: string | null;
  uow_ids: string[];
  intent?: string | null;
  implementation_details?: string | null;
  technical_constraints: string[];
}

export interface ImplementationUnitOfWorkContext {
  acceptance_criteria: string[];
  description: string;
  estimated_effort: string | null;
  primary_repository_slug: string;
  reference_repository_slugs: string[];
  technical_notes: string[];
  title: string;
  unit_of_work_id: string;
}

export interface ImplementationPlanningContext {
  overview: string | null;
  prerequisites: string[];
  integration_notes: string[];
  plan_complexity_score: number | null;
  trace_id: string | null;
}

export interface ImplementationAttachmentContext {
  id: string;
  filename: string;
  url: string;
  mime_type: string | null;
  size: number | null;
  description: string | null;
}

export interface ImplementationWorkspaceState {
  repository_access: ImplementationRepositoryAccess;
  selected_repos: string[];
  selected_branches: Record<string, string>;
  selected_base_shas: Record<string, string>;
  snapshot: JsonValue | null;
  primary_repository_slug: string | null;
  touchpoints: JsonValue | null;
  prior_node_summaries?: ImplementationPriorNodeSummary[];
}

export interface ImplementationRepositoryAccess {
  modifiable_repositories: string[];
  readonly_repositories: string[];
}

export interface ImplementationContext {
  subtask: ImplementationSubtaskContext | null;
  units_of_work?: ImplementationUnitOfWorkContext[] | null;
  planning_context: ImplementationPlanningContext;
  workspace_state: ImplementationWorkspaceState;
  repository_guide?: string | null;
  additional_notes?: string | null;
}

export interface ImplementationPriorNodeSummary {
  plan_item_id: string;
  plan_item_title: string | null;
  plan_item_description: string | null;
  summary: string;
  files_changed: {
    path: string;
    status?: string | null;
    added_lines?: number | null;
    removed_lines?: number | null;
    ui_ux_visual_change?: boolean | null;
  }[];
}

const implementationPriorNodeSummarySchema = z.object({
  files_changed: z.array(
    z.object({
      added_lines: z.number().nullable().optional(),
      path: z.string(),
      removed_lines: z.number().nullable().optional(),
      status: z.string().nullable().optional(),
      ui_ux_visual_change: z.boolean().nullable().optional(),
    }),
  ),
  plan_item_description: z.string().nullable(),
  plan_item_id: z.string(),
  plan_item_title: z.string().nullable(),
  summary: z.string(),
});

const implementationSubtaskContextSchema = z.object({
  attachment_refs: z.array(z.string()).default([]),
  attachments: z
    .array(
      z.object({
        description: z.string().nullable(),
        filename: z.string(),
        id: z.string(),
        mime_type: z.string().nullable(),
        size: z.number().nullable(),
        url: z.string(),
      }),
    )
    .optional(),
  dependencies: z.array(z.string()),
  description: z.string(),
  id: z.string(),
  implementation_details: z.string().nullable().optional(),
  intent: z.string().nullable().optional(),
  issue_key: z.string().nullable(),
  reference_repository_slugs: z.array(z.string()),
  repository_slug: z.string(),
  self_assessed_risk: z.string().nullable(),
  size_symbol: z.string().nullable(),
  technical_constraints: z.array(z.string()).default([]),
  title: z.string(),
  uow_ids: z.array(z.string()).default([]),
});

const implementationUnitOfWorkContextSchema = z.object({
  acceptance_criteria: z.array(z.string()).default([]),
  description: z.string(),
  estimated_effort: z.string().nullable(),
  primary_repository_slug: z.string(),
  reference_repository_slugs: z.array(z.string()).default([]),
  technical_notes: z.array(z.string()).default([]),
  title: z.string(),
  unit_of_work_id: z.string(),
});

const implementationPlanningContextSchema = z.object({
  integration_notes: z.array(z.string()),
  overview: z.string().nullable(),
  plan_complexity_score: z.number().nullable(),
  prerequisites: z.array(z.string()),
  trace_id: z.string().nullable(),
});

const implementationWorkspaceStateSchema = z.object({
  primary_repository_slug: z.string().nullable(),
  prior_node_summaries: z
    .array(implementationPriorNodeSummarySchema)
    .optional(),
  repository_access: z
    .object({
      modifiable_repositories: z.array(z.string()).default([]),
      readonly_repositories: z.array(z.string()).default([]),
    })
    .strict(),
  selected_base_shas: z.record(z.string(), z.string()),
  selected_branches: z.record(z.string(), z.string()),
  selected_repos: z.array(z.string()),
  snapshot: jsonValueSchema.nullable(),
  touchpoints: jsonValueSchema.nullable(),
});

export const implementationContextSchema = z.object({
  additional_notes: z.string().nullable().optional(),
  planning_context: implementationPlanningContextSchema,
  repository_guide: z.string().nullable().optional(),
  subtask: implementationSubtaskContextSchema.nullable(),
  units_of_work: z
    .array(implementationUnitOfWorkContextSchema)
    .nullable()
    .optional(),
  workspace_state: implementationWorkspaceStateSchema,
});

export function parseImplementationContext(
  value: unknown,
): ImplementationContext | null {
  const parsed = implementationContextSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function buildImplementationContextPrompt(
  context: ImplementationContext,
): string {
  return [
    "## Implementation Context",
    "",
    "## Repository Assignment Contract",
    "- Modify only repos in `workspace_state.repository_access.modifiable_repositories`.",
    "- Treat repos in `workspace_state.repository_access.readonly_repositories` as read-only context.",
    "JSON:",
    JSON.stringify(context, null, 2),
  ]
    .join("\n")
    .trim();
}
